import { useEffect, useRef } from 'react';
import { apiClient } from '@/lib/api';

interface AuthPromptProps {
  authUrl?: string;
  message?: string;
  onAuthComplete?: () => void;
}

export function AuthPrompt({ authUrl, message, onAuthComplete }: AuthPromptProps) {
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const popupRef = useRef<Window | null>(null);

  const handleAuth = () => {
    if (authUrl) {
      // Open OAuth flow in new window
      popupRef.current = window.open(authUrl, '_blank', 'width=600,height=700');

      // Start polling for completion
      pollIntervalRef.current = setInterval(async () => {
        try {
          // Check if user is now authenticated
          const session = await apiClient.getSession();

          if (session.user_id) {
            // Authentication complete!
            console.log('[AuthPrompt] Authentication completed!');

            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }

            // Close popup if still open
            if (popupRef.current && !popupRef.current.closed) {
              popupRef.current.close();
            }

            // Notify parent or reload
            if (onAuthComplete) {
              onAuthComplete();
            } else {
              window.location.reload();
            }
          }
        } catch (err) {
          // Still not authenticated, continue polling
          console.log('[AuthPrompt] Still waiting for authentication...');
        }
      }, 2000); // Poll every 2 seconds

      // Stop polling after 5 minutes (timeout)
      setTimeout(() => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          console.log('[AuthPrompt] Polling timeout - user may have abandoned signup');
        }
      }, 5 * 60 * 1000);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  return (
    <div className="flex items-center justify-center h-screen p-4 bg-background">
      <div className="text-center max-w-md">
        <div className="mb-6">
          <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
            <svg
              className="w-8 h-8 text-blue-600 dark:text-blue-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>

          <h2 className="text-xl font-semibold mb-2 text-foreground">
            Sign in to Crewflow
          </h2>

          <p className="text-sm text-muted-foreground mb-6">
            {message || 'Sign in to activate the Crewflow plugin and access AI-powered features.'}
          </p>
        </div>

        {authUrl && (
          <div className="space-y-3">
            <button
              onClick={handleAuth}
              className="w-full px-4 py-3 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              Sign in with OAuth
            </button>

            <p className="text-xs text-muted-foreground">
              The popup will close automatically after signing in.
            </p>
          </div>
        )}

        {!authUrl && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              No authentication URL available. Please contact your administrator.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
